/**
 * PortalInicio — a nova página de entrada do portal do parceiro (PR 5).
 *
 * Substitui o Dashboard como pouso: saudação, atalho do Atendimento,
 * Comunicados (feed da Central com vídeo, rolagem infinita e leitura
 * obrigatória) e Notificações ao lado (celular: as 3 mais recentes acima dos
 * Comunicados). O Início NÃO é tela desligável — só as SEÇÕES de dentro
 * obedecem à permissão da tela que representam (Comunicados → `central`,
 * Atendimento → `atendimento`); Notificações é para todo parceiro.
 *
 * `?post=<id>` (link de uma notificação antiga ou do e-mail) destaca UM post
 * no topo dos Comunicados e some da lista de baixo — mesmo comportamento da
 * Central de hoje (`PortalCentralFeedPage`), só que morando aqui agora.
 */
import { useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Megaphone } from "lucide-react";

import {
  AvisoLeiturasPendentes,
  CartaoAtendimento,
  CartaoDoComunicado,
  EstadoDaSecao,
  InicioLayout,
  PainelDeNotificacoes,
  SaudacaoDoInicio,
} from "@/components/portal/inicio";
import { rolarAteAsNotificacoesInsistindo } from "@/components/portal/inicio/rolarAteAsNotificacoes";
import { Button } from "@/components/ui/button";
import { usePortalAuth } from "@/contexts/PortalAuthContext";
import { isPostUnavailableError, usePortalCentralPost } from "@/hooks/usePortalCentralFeed";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import {
  useConfirmarLeituraDoInicio,
  usePortalComunicados,
  usePortalLeiturasPendentes,
} from "@/hooks/usePortalInicio";
import {
  useMarkAllPortalNotificationsRead,
  useMarkPortalNotificationRead,
  usePortalNotificationsInfinite,
  usePortalUnreadCount,
} from "@/hooks/usePortalNotifications";
import { usePortalPermissions } from "@/hooks/usePortalPermissions";
import { caminhoSeguroDoPortal } from "@/lib/portal/caminhoSeguro";
import type { PortalNotification } from "@/services/portalApi";

const TAMANHO_DA_PAGINA = 10;

/**
 * I1/M4 (revisão final) — achata as páginas de um `useInfiniteQuery` numa
 * lista só, sem repetir id. Um post publicado (ou notificação criada) ENTRE
 * dois carregamentos desloca a página seguinte em um item, e o mesmo registro
 * aparece nas duas — sem isto o React avisa de `key` duplicada e o DOM ganha
 * um `id` repetido (comunicado, notificação).
 */
function achatarPaginas<T extends { id: string }>(
  paginas: ReadonlyArray<{ items: readonly T[] }> | undefined
): T[] {
  const vistos = new Set<string>();
  const lista: T[] = [];
  for (const pagina of paginas ?? []) {
    for (const item of pagina.items) {
      if (vistos.has(item.id)) continue;
      vistos.add(item.id);
      lista.push(item);
    }
  }
  return lista;
}

export default function PortalInicio() {
  const { user } = usePortalAuth();
  const { has } = usePortalPermissions();
  const navigate = useNavigate();

  // Desenho do portal 0.6: "Ver todas" do sino leva a /portal/inicio#notificacoes — rola até o painel (a visível das duas).
  // ⚠️ `key` nas dependências: já em `#notificacoes`, a pessoa rola para longe e toca "Ver todas" de novo. O `Link` para a MESMA url
  // faz `replace` com uma location nova (`key` nova), mas o `hash` é a mesma string — só com `[hash]` o efeito não rodava e nada rolava.
  // 🔴 Chegando com o Início frio, o conteúdo (feed, comunicados) ainda não existe e a faixa rolável tem 0 px: a rolagem insiste enquanto a
  // página cresce (E2E D-2) e para no 1º gesto da pessoa, no prazo curto ou ao sair (o retorno do efeito cancela).
  const { hash, key } = useLocation();
  useEffect(() => {
    if (hash !== "#notificacoes") return;
    return rolarAteAsNotificacoesInsistindo();
  }, [hash, key]);

  const temComunicados = has("portal:central:view");
  const temAtendimento = has("portal:atendimento:view");

  // --- Comunicados (Central): feed infinito + destacado + pendências -------
  const [searchParams] = useSearchParams();
  const destacadoId = searchParams.get("post");
  const destacadoQuery = usePortalCentralPost(temComunicados ? destacadoId : null);
  const destacadoRef = useRef<HTMLDivElement>(null);

  // I2 (revisão final) — `enabled: temComunicados`: sem a permissão, nem a
  // query de feed nem a de pendências disparam (antes só o RENDER era
  // gateado, e um parceiro sem Central levava 403 em silêncio a cada visita).
  const comunicados = usePortalComunicados(TAMANHO_DA_PAGINA, temComunicados);
  const pendentes = usePortalLeiturasPendentes(temComunicados);
  const confirmar = useConfirmarLeituraDoInicio();

  // M4 (revisão final) — dedupe por id: um post publicado ENTRE dois
  // carregamentos desloca a página seguinte em um item, e o mesmo post
  // aparece nas duas páginas (`key` duplicada, `id="comunicado-…"` repetido
  // no DOM). Mesmo helper que já protege a lista de notificações (I1).
  const itensDoFeed = achatarPaginas(comunicados.data?.pages);
  // I4 (revisão final, porta o M1 da revisão do PR 4) — o destacado só sai da
  // lista de baixo depois de CARREGADO (`destacadoQuery.data`), nunca só por
  // `?post=` estar na URL. Filtrar por `destacadoId` escondia o post da lista
  // mesmo quando a busca dele falhava por um erro TRANSIENTE (rede/5xx): o
  // post ficava invisível nos dois lugares até um reload.
  const itens = destacadoQuery.data
    ? itensDoFeed.filter((item) => item.id !== destacadoId)
    : itensDoFeed;

  // M6(d) (revisão final) — memoizado: sem isto, a função inline recriava a
  // cada render (inclusive o poll de 30 s do próprio feed), o que recriava o
  // IntersectionObserver inteiro no mesmo ritmo (`useInfiniteScroll` depende
  // da IDENTIDADE de `onLoadMore` no `useEffect` que monta o observer).
  // `fetchNextPage` isolado (não `comunicados` inteiro) nas deps: o objeto
  // `comunicados` é recriado a cada render do React Query — colocá-lo nas
  // deps derrotaria a memoização.
  const { fetchNextPage: buscarProximaPaginaComunicados } = comunicados;
  const onLoadMoreComunicados = useCallback(() => {
    buscarProximaPaginaComunicados();
  }, [buscarProximaPaginaComunicados]);

  const { loaderRef } = useInfiniteScroll({
    onLoadMore: onLoadMoreComunicados,
    hasMore: !!comunicados.hasNextPage,
    isLoading: comunicados.isFetchingNextPage,
  });

  // Assim que o destacado chega, rola até ele e MOVE o foco pro artigo — o
  // parceiro veio de um link (notificação/e-mail) que prometia levar direto
  // nele. `focus({ preventScroll: true })` porque o `scrollIntoView` já
  // rolou; focar de novo com rolagem pularia a posição.
  useEffect(() => {
    if (destacadoQuery.data) {
      const raiz = destacadoRef.current;
      raiz?.scrollIntoView({ behavior: "smooth", block: "start" });
      raiz?.querySelector<HTMLElement>('[data-testid="comunicado-card"]')?.focus({
        preventScroll: true,
      });
    }
  }, [destacadoQuery.data]);

  // --- Notificações ----------------------------------------------------------
  // I1 (revisão final) — pagina por `offset` de verdade (`useInfiniteQuery`),
  // não mais por um `limit` que crescia a cada clique: aquele limite virava
  // query key nova a cada "Ver mais" (`isLoading` voltava a `true`, a lista
  // toda sumia pro esqueleto) e, na terceira página, `limit=60` estourava o
  // teto do backend (`le=50`) — 422 e a seção travava quebrada. Ver
  // `usePortalNotificationsInfinite`.
  const notificacoes = usePortalNotificationsInfinite();
  const unreadCount = usePortalUnreadCount();
  const marcarLida = useMarkPortalNotificationRead();
  const marcarTodas = useMarkAllPortalNotificationsRead();

  const itensNotificacoes = achatarPaginas(notificacoes.data?.pages);
  const temMaisNotificacoes = !!notificacoes.hasNextPage;

  function abrirNotificacao(item: PortalNotification) {
    if (!item.is_read) marcarLida.mutate(item.id);
    // Só navega para dentro do portal — `caminhoSeguroDoPortal` resolve
    // dot-segments/host antes de aprovar (defesa em profundidade, mesma
    // função do sininho).
    const destino = caminhoSeguroDoPortal(item.link);
    if (destino) navigate(destino);
  }

  function painelDeNotificacoes(variante: "coluna" | "compacta") {
    return (
      <PainelDeNotificacoes
        variante={variante}
        itens={itensNotificacoes}
        carregando={notificacoes.isLoading}
        // I1/I3 (revisão final) — só é erro de SEÇÃO quando não sobrou dado
        // nenhum (falhou já na 1ª página). Uma página seguinte que falha
        // (`isFetchNextPageError`) mantém a lista: o próprio botão "Ver mais"
        // continua no ar (`hasNextPage` não muda numa tentativa que falhou) e
        // vira o retry — sem isto, uma falha na 2ª página apagava as
        // notificações já carregadas, o mesmo defeito do I3 dos Comunicados.
        erro={notificacoes.isError && !notificacoes.data}
        aoTentarDeNovo={() => notificacoes.refetch()}
        temMais={temMaisNotificacoes}
        aoVerMais={() => notificacoes.fetchNextPage()}
        carregandoMais={notificacoes.isFetchingNextPage}
        aoAbrir={abrirNotificacao}
        aoMarcarTodas={() => marcarTodas.mutate()}
        marcandoTodas={marcarTodas.isPending}
        naoLidas={unreadCount.data ?? 0}
      />
    );
  }

  // --- Comunicados: montagem do slot -----------------------------------------
  function comunicadosDoSlot() {
    if (!temComunicados) {
      // M14 (revisão final) — sem a permissão, a seção some (comportamento
      // normal — ver o `return null`). Mas se o parceiro chegou por um link
      // de Central (`?post=`, de uma notificação antiga ou de um e-mail já
      // enviado), sumir em silêncio parece link quebrado. Um aviso de uma
      // linha é mais honesto do que uma tela que finge não ter recebido o
      // parâmetro.
      if (destacadoId) {
        return (
          <p className="text-sm text-slate-600" data-testid="comunicados-sem-acesso">
            Você não tem acesso aos comunicados.
          </p>
        );
      }
      return null;
    }

    const quantidadePendente = pendentes.data?.length ?? 0;

    return (
      <>
        <AvisoLeiturasPendentes quantidade={quantidadePendente} />

        {destacadoId && (
          <>
            {destacadoQuery.isLoading && (
              <EstadoDaSecao tipo="carregando" mensagem="Carregando publicação…" />
            )}
            {/* I4 (revisão final, porta o M1 da revisão do PR 4) — só os TRÊS
                casos fechados (404, 422, id que nunca foi UUID) viram
                "indisponível", em tom INFORMATIVO (âmbar), nunca erro
                vermelho: `isPostUnavailableError` decide. Qualquer OUTRO erro
                (rede, 5xx) é transiente — aviso neutro com "Tentar de novo". */}
            {destacadoQuery.isError &&
              (isPostUnavailableError(destacadoQuery.error) ? (
                <EstadoDaSecao
                  tipo="indisponivel"
                  mensagem="Esta publicação não está mais disponível."
                />
              ) : (
                <EstadoDaSecao
                  tipo="erro"
                  mensagem="Não foi possível carregar esta publicação."
                  aoTentarDeNovo={() => destacadoQuery.refetch()}
                />
              ))}
            {destacadoQuery.data && (
              // S2 (revisão de design) — `scroll-mt-6`: o `scrollIntoView`
              // (efeito acima) colava o cartão no topo do `main` (y=65, a
              // borda do cabeçalho do portal), com o anel de destaque
              // "cortado" em cima. 24 px = o `space-y-6` do topo da página; o
              // `main` do PortalLayout é quem rola, então `scroll-margin-top`
              // vale para o `scrollIntoView`.
              <div ref={destacadoRef} className="scroll-mt-6">
                <CartaoDoComunicado
                  post={destacadoQuery.data}
                  destacado
                  aoConfirmarLeitura={confirmar.confirmar}
                  confirmando={
                    confirmar.isPending && confirmar.variables === destacadoQuery.data.id
                  }
                />
              </div>
            )}
          </>
        )}

        {comunicados.isLoading ? (
          <EstadoDaSecao tipo="carregando" mensagem="Carregando comunicados…" />
        ) : comunicados.isError && !comunicados.data ? (
          // I3 (revisão final) — só é erro de SEÇÃO quando não sobrou NENHUM
          // dado (a 1ª página falhou). Uma falha na página SEGUINTE também
          // deixa `isError=true` no TanStack v5, mas mantém `data` com as
          // páginas já carregadas — tratada abaixo, perto do "Carregar mais",
          // sem apagar o que o parceiro já estava lendo.
          <EstadoDaSecao
            tipo="erro"
            mensagem="Não foi possível carregar os comunicados."
            aoTentarDeNovo={() => comunicados.refetch()}
          />
        ) : itens.length === 0 && !destacadoQuery.data ? (
          // M5 (revisão final) — só é vazio quando NEM a lista nem o
          // destacado têm algo: com um `?post=` resolvido e mais nada no
          // feed (`itens` já exclui o próprio destacado), "Nenhum comunicado
          // por enquanto." aparecia embaixo de um post de verdade.
          <EstadoDaSecao
            tipo="vazio"
            mensagem="Nenhum comunicado por enquanto."
            icone={<Megaphone />}
          />
        ) : (
          <>
            {/* M6(a) (revisão final) — `aria-live="polite"` anuncia pra quem
                usa leitor de tela que MAIS comunicados chegaram (o
                IntersectionObserver dispara sem clique nenhum); `aria-busy`
                pede pro leitor esperar o fetch assentar antes de anunciar. */}
            <div
              aria-live="polite"
              aria-busy={comunicados.isFetchingNextPage}
              data-testid="comunicados-lista"
              className="space-y-4"
            >
              {itens.map((item) => (
                <CartaoDoComunicado
                  key={item.id}
                  post={item}
                  aoConfirmarLeitura={confirmar.confirmar}
                  confirmando={confirmar.isPending && confirmar.variables === item.id}
                />
              ))}
            </div>

            {/* Sentinela da rolagem infinita — IntersectionObserver dispara
                `fetchNextPage` um pouco antes do fim (ver useInfiniteScroll). */}
            <div ref={loaderRef} aria-hidden="true" />

            {/* I3 (revisão final) — a página SEGUINTE falhou (rede/5xx): a
                lista já carregada fica, um aviso aparece do lado do "Carregar
                mais" e "Tentar de novo" chama `fetchNextPage()` de novo.
                `hasNextPage` não muda numa tentativa que falhou, então o
                "Carregar mais" abaixo continua no ar como segundo caminho de
                retry. */}
            {comunicados.isFetchNextPageError && (
              <EstadoDaSecao
                tipo="erro"
                mensagem="Não foi possível carregar mais comunicados."
                aoTentarDeNovo={() => comunicados.fetchNextPage()}
              />
            )}

            {comunicados.hasNextPage && (
              <Button
                type="button"
                variant="outline"
                // P4 (revisão de design) — `text-sm` explícito: o `Button` do
                // projeto nasce com `text-xs sm:text-sm`, 12 px num alvo de
                // 48 px no celular, logo abaixo de um "Ver todas" de 14 px.
                className="h-12 w-full border-emerald-200 text-sm text-emerald-800 hover:bg-emerald-50"
                onClick={() => {
                  if (!comunicados.isFetchingNextPage) comunicados.fetchNextPage();
                }}
                // M6(b) (revisão final) — `aria-disabled`, não `disabled`: o
                // nativo tira o botão da árvore de foco na hora e derruba o
                // foco do teclado pro `<body>` no meio da lista (mesmo fix
                // do "Ver mais" das notificações, I1).
                aria-disabled={comunicados.isFetchingNextPage}
                data-testid="comunicados-carregar-mais"
              >
                {comunicados.isFetchingNextPage ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    Carregando…
                  </>
                ) : (
                  "Carregar mais"
                )}
              </Button>
            )}
          </>
        )}
      </>
    );
  }

  return (
    <InicioLayout
      saudacao={
        <SaudacaoDoInicio
          nome={user?.first_name}
          razaoSocial={user?.substabelecido?.razao_social}
        />
      }
      atendimento={temAtendimento ? <CartaoAtendimento href="/portal/atendimento" /> : null}
      notificacoes={painelDeNotificacoes("coluna")}
      notificacoesCompactas={painelDeNotificacoes("compacta")}
      comunicados={comunicadosDoSlot()}
    />
  );
}
